import { z } from "zod";
import { defineStepConfig } from "../../registry/helpers";

/**
 * Send Announcement (v2)
 *
 * Input-driven sibling of `send_announcement`. Where v1 takes its recipients,
 * title, body, and channels from static config, v2 takes them from INPUT
 * sockets — so an upstream step can drive a different announcement per run (or
 * per fan-out branch). This is what the collection-thread reminder workflow uses
 * to send each Team member a personalized digest on their own channels.
 *
 * A separate step (rather than extending v1) because v1 is already in use by
 * persisted workflows whose config schema must not change.
 *
 * Config holds only what isn't per-recipient: severity level and the email
 * sender identity. Like v1, it writes a `SystemAnnouncement` event via
 * NotificationService.
 */
export const sendAnnouncementV2Step = defineStepConfig({
  type: "send_announcement_v2",
  name: "Send Announcement (v2)",
  description:
    "Send an announcement whose recipients, title, body, and channels come " +
    "from input sockets (set by an upstream step). For per-recipient or " +
    "dynamic announcements. Recipients are always specific users.",
  category: "Integration",

  inputSchema: z
    .object({
      recipientUserIds: z
        .array(z.string().uuid())
        .default([])
        .describe("Users to notify. Treated as 'specific_users' targeting."),
      extraEmails: z
        .array(z.string().email())
        .default([])
        .describe(
          "Mailboxes with no Gently user. Always emailed when present; " +
            "never written to the in-app inbox.",
        ),
      title: z.string().min(1).describe("Announcement headline."),
      body: z.string().min(1).describe("Announcement body text."),
      channels: z
        .array(z.enum(["in_app", "email"]))
        .min(1)
        .describe(
          "Delivery channels. 'in_app' writes to the bell inbox; 'email' sends " +
            "via the configured sender. At least one.",
        ),
    })
    .superRefine((val, ctx) => {
      if (val.recipientUserIds.length === 0 && val.extraEmails.length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["recipientUserIds"],
          message:
            "At least one recipientUserId or extraEmails address is required.",
        });
      }
    }),

  outputSchema: z.object({
    eventId: z.string().uuid().describe("The comm_notification_events.id row."),
    recipientCount: z
      .number()
      .int()
      .nonnegative()
      .describe(
        "Number of UserNotification rows inserted by this run. 0 on " +
          "idempotent replay or when no recipients resolved.",
      ),
    deduplicated: z
      .boolean()
      .describe(
        "True when the call matched an existing event via the auto-generated " +
          "idempotency key (a prior step run already wrote it).",
      ),
  }),

  configSchema: z
    .object({
      level: z
        .enum(["info", "warn", "critical"])
        .default("info")
        .describe(
          "Severity tag for the renderer (icon color, prominence). " +
            "Defaults to 'info'.",
        ),

      senderKind: z
        .enum(["default_sendgrid", "microsoft_graph"])
        .default("default_sendgrid")
        .describe(
          "Email backend used when 'email' is among the channels. " +
            "'default_sendgrid' uses the org-wide verified SendGrid sender; " +
            "'microsoft_graph' sends from a registered mailbox UPN.",
        ),

      senderMailbox: z
        .preprocess(
          // The editor persists an empty-string default for the UPN field
          // whenever senderKind flips back to SendGrid. Coerce those into
          // `undefined` so `.optional()` accepts them.
          (v) => (typeof v === "string" && v.trim() === "" ? undefined : v),
          z.string().min(1).optional(),
        )
        .describe(
          "Mailbox UPN to send from when senderKind is 'microsoft_graph'. " +
            "Must be an enabled UPN under one of the org's EmailIngestConfigs. " +
            "Ignored for SendGrid.",
        ),
    })
    .superRefine((val, ctx) => {
      // Channels arrive at run time (input socket), so we can't gate on email
      // here — if a Graph sender is chosen, require its mailbox unconditionally.
      if (val.senderKind === "microsoft_graph" && !val.senderMailbox) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["senderMailbox"],
          message:
            "senderMailbox is required when senderKind is 'microsoft_graph'.",
        });
      }
    }),

  emits: [] as const,

  execution: {
    startToCloseTimeout: "30 seconds",
    retry: {
      maximumAttempts: 3,
      backoffCoefficient: 2,
      initialInterval: "5 seconds",
    },
  },
});
